import './assets/main.css';
import 'primeicons/primeicons.css';
import '@dev_mat/floating-ui-tour/style.css';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import { installPreloadErrorRecovery } from './router/chunkReloadRecovery';
import { apolloClient } from './apolloClient';
import { DefaultApolloClient } from '@vue/apollo-composable';
import ToastService from 'primevue/toastservice';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import Aura from '@primeuix/themes/aura';
import * as Sentry from '@sentry/vue';
import { SENTRY_DENY_URLS, SENTRY_IGNORE_ERRORS, dropRecoveredChunkErrors } from './sentryFilters';
import { CURRENT_VERSION } from '@/composables/useAppVersion';
// Import FontAwesome core
import { library } from '@fortawesome/fontawesome-svg-core';

// Import FontAwesome icon components
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

// Import specific icons
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';

// Add icons to the library
library.add(fas, far, fab);

const app = createApp({
  setup() {
    provide(DefaultApolloClient, apolloClient);
  },
  render: () => h(App),
});

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  sendDefaultPii: true,
  // Without these, every build lands in one undifferentiated bucket and an
  // error from a stale tab is indistinguishable from a live regression —
  // the exact ambiguity that made JAVASCRIPT-VUE-46 unreadable. Shares the
  // build hash `useAppVersion` already polls `/version.json` for.
  release: CURRENT_VERSION,
  environment: import.meta.env.MODE,
  // Not ours, or not bugs: a third-party beacon's crashes, requests that never
  // reached the API, and stale chunks we already reloaded for. See
  // sentryFilters.ts for the issues each one closes.
  denyUrls: SENTRY_DENY_URLS,
  ignoreErrors: SENTRY_IGNORE_ERRORS,
  beforeSend: dropRecoveredChunkErrors,
});

// A route chunk the running deploy no longer serves is reloaded for by the
// router (chunkReloadRecovery.ts). This catches every other stale chunk: CSS
// preloads, and the lazy components and modules imported outside the router.
installPreloadErrorRecovery();

app.use(createPinia());
app.component('font-awesome-icon', FontAwesomeIcon);
app.use(ToastService);
app.use(ConfirmationService);
app.use(PrimeVue, {
  theme: {
    preset: Aura,
    options: {
      prefix: 'p',
      darkModeSelector: 'none',
      cssLayer: false,
    },
  },
});
import VCalendar from 'v-calendar';
import 'v-calendar/style.css';
import { useBranding } from '@/composables/useBranding';

app.use(VCalendar, {});

import { registerServiceWorker } from '@/services/pushNotification';

// Google Analytics — production site only (excludes staging & local dev)
if (import.meta.env.PROD && window.location.hostname === 'gradeverse.com') {
  const gtagScript = document.createElement('script');
  gtagScript.async = true;
  gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-SVQ7JD1EW0';
  document.head.appendChild(gtagScript);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.gtag = (...args: unknown[]) => {
    w.dataLayer.push(args);
  };
  w.gtag('js', new Date());
  w.gtag('config', 'G-SVQ7JD1EW0');
}

const { resolveAndApply } = useBranding();
const brandingReady = resolveAndApply();
const timeout = new Promise<void>(resolve => setTimeout(resolve, 3000));
Promise.race([brandingReady, timeout]).then(() => {
  app.use(router);
  app.mount('#app');
  registerServiceWorker();
});
